import React from 'react';
import { LayoutGrid, List, Layers, Award } from 'lucide-react';
import { RESULT_VIEW } from '../registry/formatRegistry';
import ViewToggle from './ViewToggle';
import BracketBoard from './brackets/BracketBoard';
import CompassGrid from './compass/CompassGrid';
import MatchListView from './lists/MatchListView';
import GroupStandingsPanel from './lists/GroupStandingsPanel';
import RegionalBracketView from './regional/RegionalBracketView';
import PagePlayoffView from './pageplayoff/PagePlayoffView';
import CrossGroupView from './crossgroup/CrossGroupView';
import LadderView from './ladder/LadderView';
import { applyTieFormatToRounds } from '../generators/tieFormats';

/**
 * `tieFormat`/`tieOverrides` are read-only here — they only affect how a
 * match's `subMatches` are computed for display. Editing them happens
 * exclusively through GeneratedScheduleStep's "MLP Settings" modal, not
 * inline in this view (see MLPSettingsModal.jsx).
 *
 * `matchOrderOverrides`/`reorderRow`, `roundOverrides`/`applyRoundOverride`,
 * and `rowOverrides`/`applyRowOverride` are the hook's raw state + callback
 * for each MatchListView feature (row reorder; the round/match gear-icon
 * settings modal), threaded through here and bound to a `listKey` per
 * MatchListView call site below (a format can render more than one —
 * per-direction, per-region, ...) so each list's own overrides are stored
 * under its own key and never collide. `courts`/`sportType` are global —
 * the same court list and sport apply to every list a format renders.
 */
const ScheduleResultView = ({
  format,
  schedule,
  viewMode,
  onViewModeChange,
  tieFormat,
  tieOverrides,
  matchOrderOverrides,
  reorderRow,
  courts,
  sportType,
  tournamentMatchSettings,
  roundOverrides,
  rowOverrides,
  applyRoundOverride,
  applyRowOverride,
  filters,
  currentUserId,
  onClearFilters,
  tournamentVO,
  startScoring,
  openLiveGame,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addRound,
  addMatch,
  fetchAllParticipants,
  participantType,
}) => {
  if (!schedule) return null;

  const listProps = (listKey) => ({
    orderOverrides: matchOrderOverrides?.[listKey],
    onReorderRow: (roundNumber, order) => reorderRow(listKey, roundNumber, order),
    courts,
    sportType,
    tournamentMatchSettings,
    roundOverrides: roundOverrides?.[listKey] || {},
    rowOverrides: rowOverrides?.[listKey] || {},
    onApplyRoundOverride: (roundNumber, override, clearRowKeys, rowLiveDataIds) => applyRoundOverride(listKey, roundNumber, override, clearRowKeys, rowLiveDataIds),
    onApplyRowOverride: (rowKey, override, liveData) => applyRowOverride(listKey, rowKey, override, liveData),
    filters,
    currentUserId,
    onClearFilters,
    tournamentVO,
    startScoring,
    openLiveGame,
    canManageThisSchedule,
    updateMatchSchedule,
    deleteMatchRow,
    assignMatchParticipants,
    addMatch,
    fetchAllParticipants,
    participantType,
  });

  // Cross-Group Duals — renders straight through to the same MatchListView
  // every other format's List view uses, no Teams/Matches toggle.
  if (format.id === 'cross_group') {
    return (
      <CrossGroupView
        schedule={schedule}
        tieFormat={tieFormat}
        tieOverrides={tieOverrides}
        matchOrderOverrides={matchOrderOverrides}
        reorderRow={reorderRow}
        courts={courts}
        sportType={sportType}
        tournamentMatchSettings={tournamentMatchSettings}
        roundOverrides={roundOverrides}
        rowOverrides={rowOverrides}
        applyRoundOverride={applyRoundOverride}
        applyRowOverride={applyRowOverride}
        filters={filters}
        currentUserId={currentUserId}
        onClearFilters={onClearFilters}
        tournamentVO={tournamentVO}
        startScoring={startScoring}
        openLiveGame={openLiveGame}
        canManageThisSchedule={canManageThisSchedule}
        updateMatchSchedule={updateMatchSchedule}
        deleteMatchRow={deleteMatchRow}
        assignMatchParticipants={assignMatchParticipants}
        addRound={addRound}
        addMatch={addMatch}
        fetchAllParticipants={fetchAllParticipants}
        participantType={participantType}
      />
    );
  }

  // Ladder — its own Ladder/Matches view (the ranked standings panel a plain
  // match list doesn't have), same reasoning as Cross-Group above.
  if (format.id === 'ladder') {
    return (
      <LadderView
        schedule={schedule}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        tieFormat={tieFormat}
        tieOverrides={tieOverrides}
        matchOrderOverrides={matchOrderOverrides}
        reorderRow={reorderRow}
        courts={courts}
        sportType={sportType}
        tournamentMatchSettings={tournamentMatchSettings}
        roundOverrides={roundOverrides}
        rowOverrides={rowOverrides}
        applyRoundOverride={applyRoundOverride}
        applyRowOverride={applyRowOverride}
        filters={filters}
        currentUserId={currentUserId}
        onClearFilters={onClearFilters}
        tournamentVO={tournamentVO}
        startScoring={startScoring}
        openLiveGame={openLiveGame}
        canManageThisSchedule={canManageThisSchedule}
        updateMatchSchedule={updateMatchSchedule}
        deleteMatchRow={deleteMatchRow}
        assignMatchParticipants={assignMatchParticipants}
      />
    );
  }

  if (format.resultView === RESULT_VIEW.LIST) {
    // Round Robin (and any other pure-list format) can be scheduled with
    // 'teams' participants — MLP Slate can apply here the same way it does
    // for Cross-Group, since it's a property of individual matches, not of
    // round_robin as a structure.
    const rounds = applyTieFormatToRounds(schedule.rounds, tieFormat, tieOverrides);
    return <MatchListView rounds={rounds} {...listProps('main')} />;
  }

  if (format.resultView === RESULT_VIEW.HYBRID && format.id === 'page_playoff') {
    return (
      <div>
        <div className="mb-4">
          <ViewToggle
            value={viewMode}
            onChange={onViewModeChange}
            options={[
              { value: 'league', label: 'League', icon: List },
              { value: 'playoffs', label: 'Playoffs', icon: Award },
              { value: 'list', label: 'List', icon: List },
            ]}
          />
        </div>
        {viewMode === 'league' ? (
          <MatchListView rounds={applyTieFormatToRounds(schedule.leagueRounds, tieFormat, tieOverrides)} title="League Stage" {...listProps('league')} />
        ) : viewMode === 'playoffs' ? (
          <PagePlayoffView playoff={schedule.playoff} />
        ) : (
          <MatchListView
            rounds={applyTieFormatToRounds(schedule.executionOrder, tieFormat, tieOverrides)}
            title="Full Schedule — Order of Execution"
            {...listProps('full')}
          />
        )}
      </div>
    );
  }

  if (format.resultView === RESULT_VIEW.HYBRID) {
    return (
      <div>
        <div className="mb-4">
          <ViewToggle
            value={viewMode}
            onChange={onViewModeChange}
            options={[
              { value: 'groups', label: 'Groups', icon: Layers },
              { value: 'bracket', label: 'Bracket', icon: LayoutGrid },
              { value: 'list', label: 'List', icon: List },
            ]}
          />
        </div>
        {viewMode === 'groups' ? (
          <GroupStandingsPanel
            groupStage={schedule.groupStage}
            advancePerGroup={schedule.advancePerGroup}
            matchOrderOverrides={matchOrderOverrides}
            reorderRow={reorderRow}
            courts={courts}
            sportType={sportType}
            tournamentMatchSettings={tournamentMatchSettings}
            roundOverrides={roundOverrides}
            rowOverrides={rowOverrides}
            applyRoundOverride={applyRoundOverride}
            applyRowOverride={applyRowOverride}
            filters={filters}
            currentUserId={currentUserId}
            onClearFilters={onClearFilters}
            tournamentVO={tournamentVO}
            startScoring={startScoring}
            openLiveGame={openLiveGame}
            canManageThisSchedule={canManageThisSchedule}
            updateMatchSchedule={updateMatchSchedule}
            deleteMatchRow={deleteMatchRow}
            assignMatchParticipants={assignMatchParticipants}
            addMatch={addMatch}
            fetchAllParticipants={fetchAllParticipants}
            participantType={participantType}
          />
        ) : viewMode === 'bracket' ? (
          <BracketBoard rounds={schedule.knockout} title="Knockout Bracket" />
        ) : (
          <MatchListView
            rounds={applyTieFormatToRounds(schedule.executionOrder, tieFormat, tieOverrides)}
            title="Full Schedule — Order of Execution"
            {...listProps('full')}
          />
        )}
      </div>
    );
  }

  // GRAPHICAL — single_elimination, compass_draw, or regional_bracket
  const isCompass = format.id === 'compass_draw';
  const isRegional = format.id === 'regional_bracket';
  return (
    <div>
      <div className="mb-4">
        <ViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          options={[
            { value: 'bracket', label: 'Bracket', icon: LayoutGrid },
            { value: 'list', label: 'List', icon: List },
          ]}
        />
      </div>
      {viewMode === 'bracket' ? (
        isCompass ? (
          <CompassGrid groups={schedule.groups} activeDirections={schedule.activeDirections} />
        ) : isRegional ? (
          <RegionalBracketView regions={schedule.regions} finalStage={schedule.finalStage} />
        ) : (
          <BracketBoard rounds={schedule.rounds} />
        )
      ) : isCompass ? (
        <div className="space-y-6">
          {schedule.activeDirections.map((dir) => (
            <MatchListView
              key={dir}
              rounds={applyTieFormatToRounds(schedule.groups[dir].rounds, tieFormat, tieOverrides)}
              title={dir === 'E' ? 'Main Draw' : dir}
              {...listProps(`compass:${dir}`)}
            />
          ))}
        </div>
      ) : isRegional ? (
        <div className="space-y-6">
          {schedule.regions.map((region) => (
            <MatchListView
              key={region.label}
              rounds={applyTieFormatToRounds(region.rounds, tieFormat, tieOverrides)}
              title={region.label}
              {...listProps(`region:${region.label}`)}
            />
          ))}
          <MatchListView rounds={applyTieFormatToRounds(schedule.finalStage, tieFormat, tieOverrides)} title="Final Stage" {...listProps('final')} />
        </div>
      ) : (
        <MatchListView rounds={applyTieFormatToRounds(schedule.rounds, tieFormat, tieOverrides)} {...listProps('main')} />
      )}
    </div>
  );
};

export default ScheduleResultView;
